'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';

import cx from 'classnames';
import PropTypes from 'prop-types';

import ButtonCTA from 'components/ui/ButtonCTA';
import Icon from 'components/ui/Icon';
import Img from 'components/ui/Img';
import Text from 'components/ui/Text';

import { useTheme } from 'hooks/useTheme';
import { blockRootProps } from 'utils';

import Lockup from '../shared/Lockup';

import styles from './BlockDeckSlider.module.scss';

const coverHeadlineOverrides = {
    'makers-reserve': {
        baseTheme: 'makers30',
        themes: { large: 'makers60' },
    },
    default: {
        baseTheme: 'displayXSmall',
        themes: { large: 'displayLarge' },
    },
};

const headlineOverrides = {
    'makers-reserve': {
        baseTheme: 'makers24',
        themes: { large: 'makers48' },
    },
};

const slideEyebrowOverrides = {
    'makers-reserve': {
        baseTheme: 'beverlyLabelMedium',
        themes: { large: 'beverlyLabelLarge' },
    },
    default: {
        baseTheme: 'labelSmallAlt',
        themes: {
            large: 'labelMediumAlt',
        },
    },
};

const bodyOverrides = {
    'makers-reserve': {
        baseTheme: 'bodyXSmall',
        themes: { large: 'bodyMedium' },
    },
};

const Tab = ({ title, ...rest }) => {
    return (
        <button className={styles.slideTab} {...rest}>
            <Text
                as="h2"
                className={styles.slideTabText}
                baseTheme="labelSmallAlt"
                themes={{ large: 'labelSmallAlt' }}
            >
                {title}
            </Text>
        </button>
    );
};

Tab.propTypes = {
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
};

const BlockDeckSlider = ({
    __typename,
    anchorId,
    coverIcon,
    coverHeading,
    coverCopy,
    coverCtaText,
    slides,
    coverTitle,
    coverTabButtonColor,
    coverBackgroundImage,
    coverBackgroundImagePortrait,
    coverTextColor,
    coverTitleColor,
    slideTitleColor,
}) => {
    coverTextColor ??= 'cream';
    coverTitleColor ??= 'cream';
    slideTitleColor ??= 'cream';

    const ref = useRef(null);

    const [globalTheme] = useTheme();

    const [currentIndex, setIndex] = useState(0);
    const [currentHoverIndex, setHoverIndex] = useState(0);

    const { width } = useWindowSize(0);

    useEffect(() => {
        ref?.current?.style.setProperty('--screenWidth', width);
    }, [width]);

    return (
        <section
            {...blockRootProps(anchorId, __typename)}
            ref={ref}
            className={cx(styles.root, styles[`theme--${globalTheme}`])}
            style={{
                '--slideTotal': slides?.length || 0,
            }}
        >
            <div className={styles.slides}>
                <div
                    style={{
                        '--coverTabButtonColor': coverTabButtonColor,
                        '--titleColor': `var(--colors-${coverTitleColor})`,
                    }}
                    className={cx(styles.slide, {
                        [styles[`slide__current`]]: 0 === currentIndex,
                    })}
                >
                    <figure className={styles.coverImage}>
                        {coverBackgroundImage?.url &&
                            coverBackgroundImagePortrait?.url && (
                                <Img
                                    className={styles.image}
                                    src={coverBackgroundImagePortrait.url}
                                    width={coverBackgroundImagePortrait?.width}
                                    height={
                                        coverBackgroundImagePortrait?.height
                                    }
                                    fallbackImageWidth={900}
                                    alt={
                                        coverBackgroundImagePortrait.description ||
                                        ''
                                    }
                                    customSources={[
                                        {
                                            breakpoint: 1024,
                                            src: coverBackgroundImage.url,
                                            imageWidth: 1600,
                                        },
                                        {
                                            breakpoint: 768,
                                            src: coverBackgroundImage.url,
                                            imageWidth: 1440,
                                        },
                                        {
                                            src: coverBackgroundImagePortrait.url,
                                            imageWidth: 900,
                                        },
                                    ]}
                                />
                            )}
                    </figure>
                    <div
                        className={cx(
                            styles.slideContent,
                            styles.slideContentCover
                        )}
                        style={{
                            '--textColor': coverTextColor
                                ? `var(--colors-${coverTextColor})`
                                : ' `var(--colors-cream)`',
                        }}
                    >
                        {coverTitle && (
                            <Text.Theme
                                as="h4"
                                className={cx(styles.coverEyebrow)}
                                config={{
                                    'makers-reserve': {
                                        baseTheme: 'labelSmallAlt',
                                        themes: { large: 'labelSmallAlt' },
                                    },
                                    default: {
                                        baseTheme: 'labelSmallAlt',
                                        themes: { large: 'labelSmallAlt' },
                                    },
                                }}
                            >
                                {coverTitle}
                            </Text.Theme>
                        )}
                        <Lockup
                            icon={coverIcon}
                            headline={{
                                text: coverHeading,
                                themeOverrides:
                                    coverHeadlineOverrides[
                                        globalTheme || 'default'
                                    ],
                            }}
                            copy={{
                                text: coverCopy,
                                themeOverrides: bodyOverrides[globalTheme],
                            }}
                            theme="Medium"
                        />
                        <ButtonCTA
                            className={styles.ctaButton}
                            text={coverCtaText}
                            style={
                                globalTheme === 'makers-reserve'
                                    ? 'makersFilled-cream-brown'
                                    : 'filled-blue-cream'
                            }
                            onClick={() => {
                                setIndex(1);
                            }}
                            onMouseOver={() => {
                                setHoverIndex(null);
                            }}
                            onMouseOut={() => {
                                setHoverIndex(null);
                            }}
                        />
                        <Text
                            className={styles.touchCta}
                            baseTheme="labelSmallAlt"
                        >
                            Swipe
                        </Text>
                    </div>
                    <Tab
                        title={coverTitle}
                        onClick={() => {
                            setIndex(0);
                        }}
                    />
                </div>
                <ol className={styles.steps}>
                    {slides &&
                        slides?.map(
                            (
                                {
                                    eyebrow,
                                    heading,
                                    body,
                                    backgroundColor,
                                    textColor,
                                    slideTitle,
                                    icon2,
                                },
                                i
                            ) => (
                                <li
                                    key={`step-${i}`}
                                    style={{
                                        '--backgroundColor': backgroundColor,
                                        '--textColor': textColor
                                            ? `var(--colors-${textColor})`
                                            : ' `var(--colors-cream)`',
                                        '--slide': i + 1,
                                        '--titleColor': `var(--colors-${slideTitleColor})`,
                                        index: '',
                                    }}
                                    className={cx(styles.slide, {
                                        [styles[`slide__active`]]:
                                            i < currentIndex,
                                        [styles[`slide__current`]]:
                                            i === currentIndex - 1,
                                        [styles[`slide__hover`]]:
                                            i === currentHoverIndex - 1 &&
                                            i !== currentIndex - 1,
                                    })}
                                >
                                    <div
                                        className={cx(
                                            styles.slideContent,
                                            styles.slideContentSlides
                                        )}
                                    >
                                        <div
                                            className={styles.slideContentInner}
                                        >
                                            <Lockup
                                                eyebrow={{
                                                    text: eyebrow,
                                                    themeOverrides:
                                                        slideEyebrowOverrides[
                                                            globalTheme ||
                                                                'default'
                                                        ],
                                                }}
                                                headline={{
                                                    as: 'h3',
                                                    text: heading,
                                                    themeOverrides:
                                                        headlineOverrides[
                                                            globalTheme ||
                                                                'default'
                                                        ],
                                                }}
                                                copy={{
                                                    text: body,
                                                    themeOverrides:
                                                        bodyOverrides[
                                                            globalTheme
                                                        ],
                                                }}
                                                theme="Medium"
                                                wordLengthThreshold={
                                                    globalTheme ===
                                                    'makers-reserve'
                                                        ? 100 // effectively disables long word check
                                                        : 9
                                                }
                                            />
                                        </div>
                                        <div
                                            className={
                                                styles.slideIconContainer
                                            }
                                        >
                                            {icon2?.icon?.url && (
                                                <div
                                                    className={styles.slideIcon}
                                                >
                                                    <Icon
                                                        className={
                                                            styles.slideIcon
                                                        }
                                                        src={icon2.icon.url}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Tab
                                        title={`${`${i + 1}`.padStart(
                                            2,
                                            '0'
                                        )} | ${slideTitle || ''}`}
                                        onClick={() => {
                                            setIndex(i + 1);
                                        }}
                                        onMouseOver={() => {
                                            setHoverIndex(i + 1);
                                        }}
                                        onMouseOut={() => {
                                            setHoverIndex(null);
                                        }}
                                    />
                                </li>
                            )
                        )}
                </ol>
            </div>
        </section>
    );
};

BlockDeckSlider.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    coverIcon: PropTypes.object,
    coverEyebrow: PropTypes.string,
    coverHeading: PropTypes.object.isRequired,
    coverCopy: PropTypes.object.isRequired,
    coverCtaText: PropTypes.string.isRequired,
    coverCtaTheme: PropTypes.string,
    coverTextColor: PropTypes.string,
    slides: PropTypes.array.isRequired,
    coverTabButtonColor: PropTypes.string,
    coverBackgroundImage: PropTypes.object,
    coverBackgroundImagePortrait: PropTypes.object,
    coverTitle: PropTypes.string.isRequired,
    coverTitleColor: PropTypes.oneOf(['cream', 'blue']),
    slideTitleColor: PropTypes.oneOf(['cream', 'blue']),
};

export default BlockDeckSlider;
